<template>
    <div>
        <b-card>
            <validation-observer ref="changPasswordForm">
                <!-- form -->
                <b-form>
                    <b-row>
                        <!-- old password -->
                        <b-col class="offset-3" md="6">
                            <b-form-group
                                label="Old Password"
                                label-for="account-old-password">
                                <validation-provider
                                    #default="{ errors }"
                                    name="Old Password"
                                    rules="required">
                                    <b-input-group class="input-group-merge">
                                        <b-form-input
                                            id="account-old-password"
                                            v-model="passwordValueOld"
                                            :type="passwordFieldTypeOld"
                                            name="old-password"
                                            placeholder="Old Password"
                                        />
                                        <b-input-group-append is-text>
                                            <feather-icon
                                                :icon="passwordToggleIconOld"
                                                class="cursor-pointer"
                                                @click="togglePasswordOld"
                                            />
                                        </b-input-group-append>
                                    </b-input-group>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>

                            </b-form-group>
                        </b-col>
                        <!--/ old password -->
                        <!-- new password -->
                        <b-col class="offset-3" md="6">
                            <b-form-group
                                label="New Password"
                                label-for="account-new-password"
                            >
                                <b-input-group class="input-group-merge">
                                    <b-form-input
                                        id="account-new-password"
                                        v-model="newPasswordValue"
                                        :type="passwordFieldTypeNew"
                                        name="new-password"
                                        placeholder="New Password"
                                    />
                                    <b-input-group-append is-text>
                                        <feather-icon
                                            :icon="passwordToggleIconNew"
                                            class="cursor-pointer"
                                            @click="togglePasswordNew"
                                        />
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                        <!--/ new password -->

                        <!-- retype password -->
                        <b-col class="offset-3" md="6">
                            <b-form-group
                                label="Confirm New Password"
                                label-for="account-retype-new-password"
                            >
                                <b-input-group class="input-group-merge">
                                    <b-form-input
                                        id="account-retype-new-password"
                                        v-model="RetypePassword"
                                        :type="passwordFieldTypeRetype"
                                        name="retype-password"
                                        placeholder="New Password"
                                    />
                                    <b-input-group-append is-text>
                                        <feather-icon
                                            :icon="passwordToggleIconRetype"
                                            class="cursor-pointer"
                                            @click="togglePasswordRetype"
                                        />
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                        <!--/ retype password -->

                        <!-- buttons -->
                        <b-col class="offset-3" cols="12">
                            <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                class="mt-1 mr-1"
                                variant="primary"
                                type="button"
                                :disabled="isSubmitting"
                                @click.prevent="changePassword">
                                {{ isSubmitting ? 'Saving...' : 'Save changes' }}
                            </b-button>
                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="mt-1"
                                type="button"
                                variant="outline-secondary"
                                @click.prevent="resetFormFields"
                            >
                                Reset
                            </b-button>
                        </b-col>
                        <!--/ buttons -->
                    </b-row>
                </b-form>
            </validation-observer>
        </b-card>

        <b-card class="mt-2">
            <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="mb-0">Active Sessions</h5>
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    size="sm"
                    variant="outline-primary"
                    type="button"
                    :disabled="sessionsLoading"
                    @click="fetchSessions"
                >
                    {{ sessionsLoading ? 'Refreshing...' : 'Refresh' }}
                </b-button>
            </div>
            <b-table
                :items="sessions"
                :fields="sessionFields"
                :busy="sessionsLoading"
                small
                responsive="sm"
                show-empty
                :empty-text="sessionsLoading ? 'Loading sessions...' : 'No active sessions found'"
                primary-key="id"
                class="mb-0"
            >
                <template #table-busy>
                    <div class="text-center text-primary my-2">
                        <b-spinner small class="mr-1" />
                        <span>Loading sessions...</span>
                    </div>
                </template>
                <template #cell(device)="data">
                    <div>
                        <span class="font-weight-bold">{{ data.item.device_type || 'Unknown Device' }}</span>
                        <small class="text-muted d-block">{{ formatSessionDetails(data.item) }}</small>
                    </div>
                </template>
                <template #cell(ip_address)="data">
                    <div>
                        <span>{{ data.item.ip_address || 'Unknown IP' }}</span>
                        <small
                            v-if="data.item.last_known_location"
                            class="text-muted d-block"
                        >
                            {{ data.item.last_known_location }}
                        </small>
                    </div>
                </template>
                <template #cell(last_used_at)="data">
                    <div>
                        <span>{{ formatDateTime(data.item.last_used_at) }}</span>
                        <small class="text-muted d-block">Signed in {{ formatDateTime(data.item.created_at) }}</small>
                    </div>
                </template>
                <template #cell(status)="data">
                    <b-badge
                        v-if="data.item.is_current"
                        variant="success"
                    >
                        Current device
                    </b-badge>
                    <b-badge
                        v-else
                        variant="primary"
                    >
                        Active
                    </b-badge>
                </template>
                <template #cell(actions)="data">
                    <b-button
                        v-if="!data.item.is_current"
                        size="sm"
                        variant="danger"
                        type="button"
                        :disabled="sessionActionLoadingId === data.item.id"
                        @click="confirmSessionRemoval(data.item)"
                    >
                        {{ sessionActionLoadingId === data.item.id ? 'Removing...' : 'Delete session' }}
                    </b-button>
                    <b-button
                        v-else
                        size="sm"
                        variant="outline-secondary"
                        type="button"
                        disabled
                    >
                        In use
                    </b-button>
                </template>
            </b-table>
        </b-card>

        <b-modal
            id="change-password-actions-modal"
            v-model="showChangeSuccessActions"
            title="Password Updated"
            centered
            hide-footer
        >
            <p class="mb-2">
                Your password has been updated successfully. Choose how you want to continue.
            </p>
            <div class="d-flex justify-content-end">
                <b-button
                    variant="danger"
                    class="mr-1"
                    type="button"
                    :disabled="logoutAllSubmitting"
                    @click="logoutFromAllDevices"
                >
                    {{ logoutAllSubmitting ? 'Logging out…' : 'Log out from all devices' }}
                </b-button>
                <b-button
                    variant="primary"
                    type="button"
                    @click="navigateHome"
                >
                    Back to home
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import {
    BButton, BForm, BFormGroup, BFormInput, BRow, BCol, BCard, BInputGroup, BInputGroupAppend, BModal, BTable, BSpinner, BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import useJwt from "@/auth/jwt/useJwt";
import {checkRes} from "@core/services/helper";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {required} from '@validations'
import Mixin from "@core/services/mixin";
import { initialAbility } from '@/libs/acl/config'

export default {
    mixins: [
        Mixin
    ],
    components: {
        BButton,
        BForm,
        BFormGroup,
        BFormInput,
        BRow,
        BCol,
        BCard,
        BInputGroup,
        BInputGroupAppend,
        BTable,
        BSpinner,
        BBadge,
        BModal,
        // validations
        ValidationProvider,
        ValidationObserver,
    },
    directives: {
        Ripple,
    },
    data() {
        return {
            passwordValueOld: '',
            newPasswordValue: '',
            RetypePassword: '',
            passwordFieldTypeOld: 'password',
            passwordFieldTypeNew: 'password',
            passwordFieldTypeRetype: 'password',
            required,
            isSubmitting: false,
            showChangeSuccessActions: false,
            logoutAllSubmitting: false,
            sessions: [],
            sessionsLoading: false,
            sessionActionLoadingId: null,
            sessionFields: [
                { key: 'device', label: 'Device' },
                { key: 'ip_address', label: 'IP Address' },
                { key: 'last_used_at', label: 'Last Active' },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: 'Actions', thClass: 'text-right', tdClass: 'text-right' },
            ],
        }
    },
    created() {
        this.fetchSessions()
    },
    computed: {
        passwordToggleIconOld() {
            return this.passwordFieldTypeOld === 'password' ? 'EyeIcon' : 'EyeOffIcon'
        },
        passwordToggleIconNew() {
            return this.passwordFieldTypeNew === 'password' ? 'EyeIcon' : 'EyeOffIcon'
        },
        passwordToggleIconRetype() {
            return this.passwordFieldTypeRetype === 'password' ? 'EyeIcon' : 'EyeOffIcon'
        },
    },
    methods: {
        togglePasswordOld() {
            this.passwordFieldTypeOld = this.passwordFieldTypeOld === 'password' ? 'text' : 'password'
        },
        togglePasswordNew() {
            this.passwordFieldTypeNew = this.passwordFieldTypeNew === 'password' ? 'text' : 'password'
        },
        togglePasswordRetype() {
            this.passwordFieldTypeRetype = this.passwordFieldTypeRetype === 'password' ? 'text' : 'password'
        },
        async changePassword() {
            const isValid = await this.$refs.changPasswordForm.validate()
            if (!isValid) {
                return
            }

            this.isSubmitting = true
            try {
                const response = await useJwt.changePasswordIsp({
                    old_password: this.passwordValueOld,
                    new_password: this.newPasswordValue,
                    confirm_password: this.RetypePassword,
                    logout_all: false,
                })
                if (checkRes(response.data.code)) {
                    this.toastMessage('success', 'Change Password', response)
                    this.resetFormFields()
                    await this.fetchSessions()
                    this.showChangeSuccessActions = true
                } else {
                    this.toastMessage('warning', 'Change Password', response)
                }
            } catch (error) {
                this.toastMessage('danger', 'Change Password', error)
            } finally {
                this.isSubmitting = false
            }
        },
        resetFormFields() {
            this.passwordValueOld = ''
            this.newPasswordValue = ''
            this.RetypePassword = ''
            if (this.$refs.changPasswordForm) {
                this.$refs.changPasswordForm.reset()
                if (typeof this.$refs.changPasswordForm.resetFields === 'function') {
                    this.$refs.changPasswordForm.resetFields()
                }
            }
        },
        async logoutFromAllDevices() {
            if (this.logoutAllSubmitting) {
                return
            }
            this.logoutAllSubmitting = true
            try {
                const response = await useJwt.logoutAllDevicesIsp()
                if (checkRes(response.data.code)) {
                    this.toastMessage('success', 'Logout', response)
                    this.showChangeSuccessActions = false
                    this.clearUserSession()
                } else {
                    this.toastMessage('warning', 'Logout', response)
                }
            } catch (error) {
                this.toastMessage('danger', 'Logout', error)
            } finally {
                this.logoutAllSubmitting = false
            }
        },
        navigateHome() {
            this.showChangeSuccessActions = false
            this.$router.push({ name: 'dashboard-ecommerce' })
        },
        clearUserSession() {
            localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
            localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
            localStorage.removeItem('userData')
            if (this.$ability && typeof this.$ability.update === 'function') {
                this.$ability.update(initialAbility)
            }
            this.$router.push({ name: 'auth-login' })
        },
        async fetchSessions() {
            this.sessionsLoading = true
            try {
                const response = await useJwt.fetchIspSessions()
                if (checkRes(response.data.code)) {
                    const payload = response.data.data ? response.data.data.sessions : null
                    let sessionList = []
                    if (Array.isArray(payload)) {
                        sessionList = payload
                    } else if (payload && Array.isArray(payload.data)) {
                        sessionList = payload.data
                    }
                    const normalizeTimestamp = session => ({
                        ...session,
                        is_current: !!session.is_current,
                    })
                    const pickTime = session => {
                        const source = session.last_used_at || session.created_at
                        if (!source) return 0
                        const parsed = new Date(source)
                        const time = parsed.getTime()
                        return Number.isNaN(time) ? 0 : time
                    }
                    this.sessions = sessionList
                        .map(normalizeTimestamp)
                        .sort((a, b) => {
                            if (a.is_current !== b.is_current) {
                                return a.is_current ? -1 : 1
                            }
                            return pickTime(b) - pickTime(a)
                        })
                } else {
                    this.toastMessage('warning', 'Active Sessions', response)
                }
            } catch (error) {
                this.toastMessage('danger', 'Active Sessions', error)
            } finally {
                this.sessionsLoading = false
            }
        },
        formatDateTime(value) {
            if (!value) {
                return '--'
            }
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) {
                return value
            }
            return date.toLocaleString()
        },
        formatSessionDetails(session) {
            const parts = []
            if (session.platform) {
                parts.push(session.platform)
            }
            if (session.browser) {
                parts.push(session.browser)
            }
            return parts.length ? parts.join(' • ') : 'Unknown platform'
        },
        async confirmSessionRemoval(session) {
            const message = `Revoke access for ${session.device_type || 'this device'} (${session.ip_address || 'Unknown IP'})?`
            const confirmed = await this.$bvModal.msgBoxConfirm(message, {
                title: 'Revoke Session',
                size: 'sm',
                okVariant: 'danger',
                okTitle: 'Revoke',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: true,
            })
            if (confirmed) {
                await this.removeSession(session)
            }
        },
        async removeSession(session) {
            this.sessionActionLoadingId = session.id
            try {
                const response = await useJwt.deleteIspSession(session.id)
                if (checkRes(response.data.code)) {
                    this.toastMessage('success', 'Active Sessions', response)
                    const payload = response.data.data || {}
                    if (payload.logged_out) {
                        this.clearUserSession()
                        return
                    }
                    this.sessions = this.sessions.filter(item => item.id !== session.id)
                } else {
                    this.toastMessage('warning', 'Active Sessions', response)
                }
            } catch (error) {
                this.toastMessage('danger', 'Active Sessions', error)
            } finally {
                this.sessionActionLoadingId = null
            }
        },
    },
}
</script>
